import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Tabs,
  Tab,
  TextField,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Mail,
  Edit,
  CheckCircle,
  Cancel,
  FolderOpen,
  Chat,
  History,
} from "@mui/icons-material";
import { EmptyState, LoadingState } from "../../UI/States";

const JobDashboardModal = ({
  selectedJob,
  setSelectedJob,
  selectedTab,
  setSelectedTab,
}) => {
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([
    { sender: "Blake", message: "Did we get the revised numbers yet?" },
    { sender: "Nathan", message: "Not yet........" },
  ]);
  const [logEntries] = useState([
    "2025-05-10 09:21 - Job created by user Blake",
    "2025-05-11 14:12 - Revision requested",
    "2025-05-11 14:44 - File uploaded: revised_blueprint.pdf",
    "2025-05-12 08:30 - Job approved by sales",
  ]);

  const [jobFiles, setJobFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const fetchJobFiles = async () => {
    if (!selectedJob) return;

    const [jobNumber, jobType] = selectedJob.split("-");
    const year = "20" + jobNumber.substring(0, 2);

    setLoadingFiles(true);
    try {
      const response = await fetch(
        `/api/sbcf/jobfiles?job=${jobNumber}&year=${year}&jobType=${jobType}`
      );
      const data = await response.json();
      if (data?.recordset) {
        console.log(data.recordset);
        setJobFiles(data.recordset);
      } else {
        setJobFiles([]);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      setJobFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 1) {
      fetchJobFiles();
    }
  }, [selectedTab, selectedJob]);

  const handleEmail = (subject, body) => {
    const email = "truss@bigclumber.com";
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, "_blank");
  };

  const handleSendMessage = () => {
    if (message.trim() === "") return;
    setChatMessages([...chatMessages, { sender: "You", message }]);
    setMessage("");
  };

  const getDownloadUrl = (filePath) =>
    `https://cloud.bigclumber.com:3001/mitekgetfiles/file?file=${filePath}&key=IUHLONuonoidmasiodamw2i12312`;

  return (
    <Dialog
      open={!!selectedJob}
      onClose={() => setSelectedJob(null)}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Job Dashboard - {selectedJob}</DialogTitle>
      <DialogContent>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          centered
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<Mail />} label="Actions" />
          <Tab icon={<FolderOpen />} label="Files" />
          <Tab icon={<History />} label="Logs" />
        </Tabs>

        <Box sx={{ pt: 3, pb: 1 }}>
          {selectedTab === 0 && (
            <Box>
              <h3 className="mb-1 text-base font-semibold">Job Actions</h3>
              <p className="app-muted mb-4 text-sm">
                Each action opens a pre-filled email to the truss team.
              </p>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() =>
                      handleEmail(
                        `Reprice Request - Job ${selectedJob}`,
                        `Hello,\n\nPlease reprice the job with ID: ${selectedJob}.\n\nThanks,`
                      )
                    }
                  >
                    Reprice
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() =>
                      handleEmail(
                        `Revisions Needed - Job ${selectedJob}`,
                        `Hello,\n\nPlease make the following revisions to job ID: ${selectedJob}:\n- \n\nThanks,`
                      )
                    }
                  >
                    Revisions
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CheckCircle />}
                    onClick={() =>
                      handleEmail(
                        `Approval Confirmation - Job ${selectedJob}`,
                        `Hello,\n\nJob ID ${selectedJob} has been approved.\n\nThanks,`
                      )
                    }
                  >
                    Approve
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() =>
                      handleEmail(
                        `Job ${selectedJob} Rejected`,
                        `Hello,\n\nJob ID ${selectedJob} has been rejected.\nReason: [INSERT REASON HERE]\n\nThanks,`
                      )
                    }
                  >
                    Reject
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {selectedTab === 1 && (
            <Box>
              <h3 className="mb-2 text-base font-semibold">Job Files</h3>
              {loadingFiles ? (
                <LoadingState label="Loading files..." className="!py-10" />
              ) : jobFiles.length > 0 ? (
                <List className="app-surface overflow-hidden rounded-xl border !py-0">
                  {jobFiles.map((fileObj, index) => {
                    const filePath = fileObj.FileName;
                    const fileName = filePath.split("\\").pop();
                    return (
                      <ListItem
                        key={index}
                        button
                        onClick={() =>
                          window.open(getDownloadUrl(filePath), "_blank")
                        }
                        divider
                      >
                        <ListItemText primary={fileName} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <EmptyState
                  compact
                  icon={FolderOpen}
                  title="No files found for this job."
                />
              )}
            </Box>
          )}

          {selectedTab === 2 && (
            <Box>
              <h3 className="mb-2 text-base font-semibold">Activity Log</h3>
              <div
                className="app-surface rounded-xl border"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                <List dense>
                  {logEntries.map((entry, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={entry} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={() => setSelectedJob(null)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDashboardModal;
